/**
 * Schedule planner maths — turning a lineup into a day-by-day timeline and
 * finding the clashes in it.
 *
 * Kept free of React so the overlap rules can be unit-tested directly; the
 * hook and the components only render what these functions return.
 */

// A festival "day" does not end at midnight — a 1:00 AM set belongs to the
// night that started the evening before, and bucketing it into the next
// calendar day would split every headliner away from its own lineup. Sets
// starting before this hour are folded back into the previous day.
export const DAY_ROLLOVER_HOUR = 5

/** "22:30" -> 1350 minutes. Returns null when unparseable. */
export const timeToMinutes = (time) => {
  if (!time) return null
  const match = String(time).trim().match(/^(\d{1,2}):(\d{2})$/)
  if (!match) return null

  const hours = parseInt(match[1], 10)
  const minutes = parseInt(match[2], 10)
  if (hours > 23 || minutes > 59) return null

  return hours * 60 + minutes
}

/** 1350 -> "10:30 PM" for display. */
export const formatTime = (time) => {
  const minutes = timeToMinutes(time)
  if (minutes === null) return ''

  const hours24 = Math.floor(minutes / 60)
  const mins = minutes % 60
  const meridiem = hours24 >= 12 ? 'PM' : 'AM'
  const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12

  return `${hours12}:${String(mins).padStart(2, '0')} ${meridiem}`
}

export const formatTimeRange = (startTime, endTime) => {
  const start = formatTime(startTime)
  if (!start) return ''
  const end = formatTime(endTime)
  return end ? `${start} – ${end}` : start
}

/**
 * Minutes since the start of the festival day, so late-night sets sort after
 * evening ones instead of wrapping to the top of the timeline.
 */
export const toTimelineMinutes = (time) => {
  const minutes = timeToMinutes(time)
  if (minutes === null) return null
  return minutes < DAY_ROLLOVER_HOUR * 60 ? minutes + 24 * 60 : minutes
}

/** "YYYY-MM-DD" key for a date-ish value; '' when absent or invalid. */
export const toDayKey = (value) => {
  if (!value) return ''
  const date = new Date(value)
  if (isNaN(date.getTime())) return ''
  return date.toISOString().split('T')[0]
}

/** "Sat Aug 22" for day tabs. */
export const formatDayLabel = (dayKey) => {
  if (!dayKey) return 'Unscheduled'
  // Parse as UTC noon: constructing from a bare date string gives midnight
  // UTC, which renders as the previous day west of Greenwich.
  const date = new Date(`${dayKey}T12:00:00Z`)
  if (isNaN(date.getTime())) return 'Unscheduled'
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC'
  })
}

/**
 * The lineup the planner should work from.
 *
 * officialLineup is the shared source of truth; artistsSeen is the personal
 * fallback for legacy events and for entries created before the shared lineup
 * existed. Preferring the shared list keeps every attendee on one schedule.
 */
export const resolveLineup = (event) => {
  if (!event) return []
  if (Array.isArray(event.officialLineup) && event.officialLineup.length > 0) {
    return event.officialLineup
  }
  return Array.isArray(event.artistsSeen) ? event.artistsSeen : []
}

/** Same identity rule the server diff uses — casing and punctuation ignored. */
export const artistKey = (name) => String(name || '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, ' ')
  .trim()

/**
 * Builds the flat slot list the timeline renders.
 *
 * Every artist becomes a slot whether or not it has a set time; the ones
 * without times are surfaced separately so the user can fill them in rather
 * than silently vanishing from the planner.
 */
export const buildSlots = (event, picks = []) => {
  const lineup = resolveLineup(event)
  const pickByKey = new Map(
    picks.map(pick => [`${artistKey(pick.artistName)}|${toDayKey(pick.day)}`, pick])
  )

  return lineup.map((artist, index) => {
    const dayKey = toDayKey(artist.performanceDate) || toDayKey(event?.startDate)
    const key = artistKey(artist.name)
    const pick = pickByKey.get(`${key}|${dayKey}`)

    const startMinutes = toTimelineMinutes(artist.startTime)
    const endMinutes = toTimelineMinutes(artist.endTime)

    return {
      index,
      key,
      name: artist.name,
      genre: artist.genre || '',
      stage: artist.stage || '',
      dayKey,
      startTime: artist.startTime || '',
      endTime: artist.endTime || '',
      startMinutes,
      endMinutes,
      // A set with a start but no end still needs a length to draw. An hour is
      // the common festival slot and beats collapsing the block to nothing.
      effectiveEndMinutes: endMinutes !== null && startMinutes !== null && endMinutes > startMinutes
        ? endMinutes
        : (startMinutes !== null ? startMinutes + 60 : null),
      hasTime: startMinutes !== null,
      picked: !!pick,
      priority: pick?.priority || 'must',
      note: pick?.note || ''
    }
  })
}

/** Groups timed slots by day, each sorted by start time. */
export const groupSlotsByDay = (slots) => {
  const days = new Map()

  slots.forEach(slot => {
    if (!slot.hasTime) return
    if (!days.has(slot.dayKey)) days.set(slot.dayKey, [])
    days.get(slot.dayKey).push(slot)
  })

  return Array.from(days.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([dayKey, daySlots]) => ({
      dayKey,
      label: formatDayLabel(dayKey),
      slots: daySlots.sort((a, b) => a.startMinutes - b.startMinutes)
    }))
}

/**
 * Groups a lineup into day sections for the event page, the way a festival
 * flyer prints it: one block per day, earliest first.
 *
 * Every artist is returned as `{ artist, index }` where `index` is its position
 * in the ARRAY THAT WAS PASSED IN. That is load-bearing, not decoration: the
 * event page identifies an artist to toggle or comment on purely by index into
 * `event.artistsSeen` (`event.artistsSeen[editingArtistIndex]`). Regrouping the
 * artists without carrying their original indices would silently point every
 * toggle and every comment at whichever artist happened to land in that slot.
 *
 * Artists with no `performanceDate` are never dropped — they collect in
 * `undated` so a partially-dated lineup still shows everyone. `dayCount` is the
 * number of distinct days, which is what the caller uses to decide whether
 * splitting is worth doing at all.
 */
export const groupArtistsByDay = (artists = [], sharedDays = new Map()) => {
  const byDay = new Map()
  const undated = []

  artists.forEach((artist, index) => {
    const entry = { artist, index }
    // Personal first, then the shared lineup. Days are shared data: the set
    // time editor writes them to officialLineup and never mirrors them into
    // artistsSeen, so a day assigned by hand exists ONLY on the shared list.
    // Reading the personal copy alone would show no day sections at all for
    // every event whose days were entered rather than extracted.
    const dayKey = toDayKey(artist?.performanceDate) || sharedDays.get(artistKey(artist?.name)) || ''

    if (!dayKey) {
      undated.push(entry)
      return
    }

    if (!byDay.has(dayKey)) byDay.set(dayKey, [])
    byDay.get(dayKey).push(entry)
  })

  const days = Array.from(byDay.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([dayKey, entries], position) => ({
      dayKey,
      label: formatDayLabel(dayKey),
      // 1-based so the heading can read "Day 1", matching how flyers number
      // them. Derived from sorted position, not from the date itself, so a
      // lineup that starts on day 2 of a festival still reads Day 1, Day 2.
      dayNumber: position + 1,
      entries
    }))

  return { days, undated, dayCount: days.length }
}

/**
 * Every calendar day the event spans, as "YYYY-MM-DD".
 *
 * The 30-day stop is a guard, not a policy: a corrupt or absurd endDate would
 * otherwise spin this loop forever.
 */
export const eventDayOptions = (event) => {
  const start = event?.startDate ? new Date(event.startDate) : null
  if (!start || isNaN(start.getTime())) return []

  const end = event?.endDate ? new Date(event.endDate) : start
  if (isNaN(end.getTime())) return [start.toISOString().split('T')[0]]

  const days = []
  const cursor = new Date(start)
  while (cursor <= end && days.length < 30) {
    days.push(cursor.toISOString().split('T')[0])
    cursor.setDate(cursor.getDate() + 1)
  }

  return days
}

/**
 * Day-by-artist index built from the shared lineup, for `groupArtistsByDay`.
 *
 * Keyed by the same normalized artist name the server diff uses, so casing and
 * punctuation differences between the personal and shared lists still match.
 */
export const dayIndexFromLineup = (lineup = []) => {
  const index = new Map()

  lineup.forEach(artist => {
    const key = artistKey(artist?.name)
    const dayKey = toDayKey(artist?.performanceDate)
    if (key && dayKey && !index.has(key)) index.set(key, dayKey)
  })

  return index
}

/**
 * Finds overlapping pairs among the sets the user actually picked.
 *
 * Only picks are compared: the whole lineup overlaps constantly across stages,
 * so flagging that would be noise. A clash only matters once you have said you
 * intend to be at both.
 *
 * Severity follows priority — two 'must' sets colliding is a decision the user
 * has to make; anything involving a 'maybe' is informational.
 */
export const findConflicts = (slots) => {
  const picked = slots
    .filter(slot => slot.picked && slot.hasTime)
    .sort((a, b) => a.startMinutes - b.startMinutes)

  const conflicts = []

  for (let i = 0; i < picked.length; i += 1) {
    for (let j = i + 1; j < picked.length; j += 1) {
      const a = picked[i]
      const b = picked[j]

      if (a.dayKey !== b.dayKey) continue

      // Sorted by start, so once b starts after a ends nothing later can clash
      // with a either.
      if (b.startMinutes >= a.effectiveEndMinutes) break

      const overlapStart = Math.max(a.startMinutes, b.startMinutes)
      const overlapEnd = Math.min(a.effectiveEndMinutes, b.effectiveEndMinutes)
      if (overlapEnd <= overlapStart) continue

      conflicts.push({
        id: `${a.key}|${b.key}|${a.dayKey}`,
        dayKey: a.dayKey,
        a,
        b,
        overlapMinutes: overlapEnd - overlapStart,
        severity: a.priority === 'must' && b.priority === 'must' ? 'hard' : 'soft'
      })
    }
  }

  return conflicts
}

/** Set of slot keys involved in any conflict, for badge rendering. */
export const conflictKeySet = (conflicts) => {
  const keys = new Set()
  conflicts.forEach(({ a, b, dayKey }) => {
    keys.add(`${a.key}|${dayKey}`)
    keys.add(`${b.key}|${dayKey}`)
  })
  return keys
}

/**
 * Placeholder stage names offered when an event has none recorded.
 *
 * Deliberately generic: every venue names its stages differently, so these are
 * a starting point to rename rather than a taxonomy to conform to.
 */
export const DEFAULT_STAGES = ['Main Stage', 'Second Stage', 'Third Stage', 'Side Stage']

/** Row label for sets whose stage was never recorded. */
export const UNASSIGNED_STAGE = 'Unassigned'

/**
 * The ordered stage rows for the grid.
 *
 * The event's own `stages` list wins — that order is the user's choice of which
 * stage is "the main one". Any stage naming a set but missing from that list is
 * appended rather than dropped, so a flyer naming a stage nobody configured
 * still gets a row instead of silently vanishing.
 */
export const resolveStages = (event, slots = []) => {
  const configured = Array.isArray(event?.stages) ? event.stages.filter(Boolean) : []

  const seen = new Set(configured.map(stage => stage.toLowerCase()))
  const extra = []

  slots.forEach(slot => {
    const stage = (slot.stage || '').trim()
    if (!stage || seen.has(stage.toLowerCase())) return
    seen.add(stage.toLowerCase())
    extra.push(stage)
  })

  return [...configured, ...extra]
}

/**
 * Groups a day's timed sets into stage rows.
 *
 * Every configured stage keeps its row even when empty — an empty row is
 * information ("nothing on that stage yet"), and rows appearing and vanishing
 * as you move between days makes the grid hard to read.
 */
export const groupSlotsByStage = (daySlots = [], stages = []) => {
  const rows = stages.map(stage => ({ stage, slots: [] }))
  const indexByStage = new Map(stages.map((stage, index) => [stage.toLowerCase(), index]))

  const unassigned = []

  daySlots.forEach(slot => {
    const stage = (slot.stage || '').trim()
    const index = stage ? indexByStage.get(stage.toLowerCase()) : undefined

    if (index === undefined) unassigned.push(slot)
    else rows[index].slots.push(slot)
  })

  // Only shown when it has something in it — unlike a real stage, an empty
  // "Unassigned" row is pure noise.
  if (unassigned.length > 0) {
    rows.push({ stage: UNASSIGNED_STAGE, slots: unassigned, isUnassigned: true })
  }

  return rows.map(row => ({
    ...row,
    slots: row.slots.slice().sort((a, b) => a.startMinutes - b.startMinutes)
  }))
}

/**
 * Horizontal extent of the grid for a day, padded out to whole hours so the
 * header columns line up with the blocks. Null when the day has no timed sets.
 */
export const buildDayWindow = (daySlots = []) => {
  if (!daySlots || daySlots.length === 0) return null

  const earliest = Math.min(...daySlots.map(slot => slot.startMinutes))
  const latest = Math.max(...daySlots.map(slot => slot.effectiveEndMinutes))

  const startMinutes = Math.floor(earliest / 60) * 60
  const endMinutes = Math.ceil(latest / 60) * 60

  const hours = []
  for (let minutes = startMinutes; minutes <= endMinutes; minutes += 60) {
    hours.push({
      minutes,
      // Hours past midnight are stored as 24+ so they sort correctly; wrap for
      // the label only.
      label: formatTime(`${String(Math.floor(minutes / 60) % 24).padStart(2, '0')}:00`)
    })
  }

  return {
    startMinutes,
    endMinutes,
    totalMinutes: Math.max(60, endMinutes - startMinutes),
    hours
  }
}

/** Percentage offset and width for a set within a day window. */
export const slotGeometry = (slot, window) => {
  if (!window) return { left: 0, width: 0 }

  const left = ((slot.startMinutes - window.startMinutes) / window.totalMinutes) * 100
  const width = ((slot.effectiveEndMinutes - slot.startMinutes) / window.totalMinutes) * 100

  // Floor the width so a very short set is still a clickable sliver rather
  // than a zero-width element.
  return { left, width: Math.max(width, 0.6) }
}

/** Total minutes of picked music for a day — a small "how full is this" stat. */
export const plannedMinutes = (daySlots) => daySlots
  .filter(slot => slot.picked)
  .reduce((total, slot) => total + (slot.effectiveEndMinutes - slot.startMinutes), 0)

/** Gaps of at least `minGap` minutes between consecutive picked sets. */
export const findGaps = (daySlots, minGap = 30) => {
  const picked = daySlots
    .filter(slot => slot.picked)
    .sort((a, b) => a.startMinutes - b.startMinutes)

  const gaps = []
  for (let i = 0; i < picked.length - 1; i += 1) {
    const gapStart = picked[i].effectiveEndMinutes
    const gapEnd = picked[i + 1].startMinutes
    if (gapEnd - gapStart >= minGap) {
      gaps.push({
        id: `gap-${picked[i].key}-${picked[i + 1].key}`,
        startMinutes: gapStart,
        endMinutes: gapEnd,
        minutes: gapEnd - gapStart
      })
    }
  }
  return gaps
}

/** "1h 45m" / "45m" */
export const formatDuration = (minutes) => {
  if (!minutes || minutes <= 0) return ''
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  if (hours === 0) return `${mins}m`
  if (mins === 0) return `${hours}h`
  return `${hours}h ${mins}m`
}
